import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, MailWarning, Play, RefreshCw, Send, X } from "lucide-react";
import { api, formatApiError, londonDateTime } from "@/lib/api";

const STATUS_FILTERS = ["", "queued", "sending", "previewed", "sent", "delivered", "delayed", "bounced", "failed", "blocked_config", "coalesced"];
const STATUS_STYLES = {
  queued: "bg-blue-500/15 text-blue-300", sending: "bg-blue-500/15 text-blue-300",
  previewed: "bg-cream-muted/15 text-cream-muted", sent: "bg-green-500/15 text-green-400",
  delivered: "bg-green-500/15 text-green-400", delayed: "bg-amber-500/15 text-amber-300",
  bounced: "bg-red-500/15 text-red-300", failed: "bg-red-500/15 text-red-300",
  blocked_config: "bg-amber-500/15 text-amber-300", coalesced: "bg-cream-muted/15 text-cream-muted",
  suppressed: "bg-red-500/15 text-red-300",
};

const displayStatus = (e) => (e.delivery_status && ["delivered", "delayed", "bounced"].includes(e.delivery_status) ? e.delivery_status : e.status);

export default function AdminEmails() {
  const [gate, setGate] = useState(null);
  const [emails, setEmails] = useState(null);
  const [status, setStatus] = useState("");
  const [orderId, setOrderId] = useState("");
  const [preview, setPreview] = useState(null);
  const [tab, setTab] = useState("html");
  const [busy, setBusy] = useState("");
  const [templates, setTemplates] = useState([]);

  const load = useCallback(() => {
    const params = {};
    if (status) params.status = status;
    if (orderId) params.order_id = orderId;
    api.get("/admin/emails", { params }).then((r) => setEmails(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/emails/gate").then((r) => setGate(r.data)).catch(() => {});
    api.get("/admin/emails/templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, [status, orderId]);
  useEffect(() => { load(); }, [load]);

  const openTemplatePreview = (type) => {
    setTab("html");
    api.get(`/admin/emails/templates/${type}/preview`).then((r) => setPreview(r.data)).catch((e) => toast.error(formatApiError(e)));
  };

  const act = (key, fn, msg) => {
    setBusy(key);
    fn().then((r) => { toast.success(r.data?.note || msg); load(); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(""));
  };

  const openPreview = (id) => {
    setTab("html");
    api.get(`/admin/emails/${id}/preview`).then((r) => setPreview(r.data)).catch((e) => toast.error(formatApiError(e)));
  };

  return (
    <div data-testid="admin-emails-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif text-2xl font-bold text-cream-text">Transactional email outbox</h1>
        <button onClick={() => act("worker", () => api.post("/admin/emails/run-worker"), "Worker pass complete")} disabled={!!busy}
          className="inline-flex items-center gap-1.5 rounded-full border border-choco-line px-4 py-2 text-xs font-bold text-cream-text hover:border-gold hover:text-gold disabled:opacity-50" data-testid="run-worker-button">
          <Play className="h-3.5 w-3.5" aria-hidden="true" /> Run worker now
        </button>
      </div>

      {gate && (
        <div className="mt-4 rounded-3xl border border-amber-500/40 bg-amber-500/10 p-5" data-testid="email-gate-banner">
          <p className="flex items-center gap-2 text-sm font-bold text-amber-300">
            <MailWarning className="h-4 w-4" aria-hidden="true" />
            TEST MODE — no real customer email is being sent
            <span className="rounded-full bg-amber-500/20 px-2.5 py-0.5 text-[11px]" data-testid="email-mode-badge">{gate.mode}</span>
          </p>
          <p className="mt-1 text-xs text-cream-muted">
            Order emails are rendered and stored for preview only. Customer delivery stays blocked until the owner verifies a sending domain (SPF/DKIM/DMARC) and approves go-live. Manual actions below are idempotent, template-only (no free-form HTML) and never change order or payment state.
          </p>
        </div>
      )}

      {templates.length > 0 && (
        <div className="mt-5 rounded-3xl border border-choco-line bg-choco-light p-5" data-testid="template-previews">
          <h2 className="font-serif text-base font-semibold text-cream-text">Preview the email templates</h2>
          <p className="mt-1 text-xs text-cream-muted">Sample data only — previewing never sends anything.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {templates.map((t) => (
              <button key={t.type} onClick={() => openTemplatePreview(t.type)}
                className="min-h-[40px] rounded-full border border-choco-line px-3.5 py-1.5 text-[11px] font-bold capitalize text-cream-muted hover:border-gold hover:text-gold"
                data-testid={`template-preview-${t.type}`}>
                {t.type.replace(/_/g, " ")}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-2" data-testid="email-status-filters">
        {STATUS_FILTERS.map((s) => (
          <button key={s || "all"} onClick={() => setStatus(s)}
            className={`rounded-full px-3.5 py-1.5 text-xs font-bold capitalize ${status === s ? "bg-gold text-choco" : "border border-choco-line text-cream-muted hover:text-gold"}`}
            data-testid={`email-filter-${s || "all"}`}>
            {(s || "all").replace(/_/g, " ")}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-3" data-testid="emails-list">
        {emails === null && <p className="text-sm text-cream-muted">Loading…</p>}
        {emails?.length === 0 && (
          <p className="rounded-3xl border border-choco-line bg-choco-light p-8 text-center text-sm text-cream-muted" data-testid="emails-empty">No emails found.</p>
        )}
        {emails?.map((e) => {
          const shown = displayStatus(e);
          return (
            <div key={e.id} className="rounded-2xl border border-choco-line bg-choco-light p-4" data-testid={`email-row-${e.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-cream-text">
                    {e.type.replace(/_/g, " ")}
                    <span className={`ml-2 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${STATUS_STYLES[shown] || "bg-cream-muted/15 text-cream-muted"}`} data-testid={`email-status-${e.id}`}>
                      {shown.replace(/_/g, " ")}
                    </span>
                  </p>
                  <p className="mt-0.5 text-xs text-cream-muted">
                    To {e.recipient} · {londonDateTime(e.created_at)}{e.attempts ? ` · ${e.attempts} attempt${e.attempts === 1 ? "" : "s"}` : ""}
                  </p>
                  {e.last_error && <p className="mt-0.5 text-xs text-amber-300/80">{e.last_error}</p>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={() => openPreview(e.id)} className="inline-flex items-center gap-1 rounded-full border border-choco-line px-3 py-1.5 text-[11px] font-bold text-cream-text hover:border-gold hover:text-gold" data-testid={`email-preview-${e.id}`}>
                    <Eye className="h-3 w-3" aria-hidden="true" /> Preview
                  </button>
                  {["failed", "blocked_config"].includes(e.status) && (
                    <button disabled={!!busy} onClick={() => act(e.id, () => api.post(`/admin/emails/${e.id}/retry`), "Re-queued")} className="inline-flex items-center gap-1 rounded-full border border-choco-line px-3 py-1.5 text-[11px] font-bold text-cream-text hover:border-gold hover:text-gold disabled:opacity-50" data-testid={`email-retry-${e.id}`}>
                      <RefreshCw className="h-3 w-3" aria-hidden="true" /> Retry
                    </button>
                  )}
                  {["previewed", "failed", "blocked_config", "sent"].includes(e.status) && (
                    <button disabled={!!busy} onClick={() => act(e.id, () => api.post(`/admin/emails/${e.id}/send-test`), "Test send queued")} className="inline-flex items-center gap-1 rounded-full border border-choco-line px-3 py-1.5 text-[11px] font-bold text-cream-text hover:border-gold hover:text-gold disabled:opacity-50" data-testid={`email-test-send-${e.id}`}>
                      <Send className="h-3 w-3" aria-hidden="true" /> Test send
                    </button>
                  )}
                  {e.order_id && e.order_exists && (
                    <button disabled={!!busy} onClick={() => act(e.id, () => api.post(`/admin/emails/orders/${e.order_id}/resend-template`, { type: e.type }), "Fresh copy queued")} className="inline-flex items-center gap-1 rounded-full border border-choco-line px-3 py-1.5 text-[11px] font-bold text-cream-text hover:border-gold hover:text-gold disabled:opacity-50" data-testid={`email-resend-${e.id}`}>
                      <RefreshCw className="h-3 w-3" aria-hidden="true" /> Resend copy
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-choco/80 p-4" role="dialog" aria-modal="true" aria-label="Email preview" data-testid="email-preview-modal">
          <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-3xl border border-choco-line bg-choco-light p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-serif text-lg font-semibold text-cream-text" data-testid="email-preview-subject">{preview.subject}</h2>
                <p className="text-xs text-amber-300">Preview of the stored template — no arbitrary HTML can be composed or sent.</p>
              </div>
              <button onClick={() => setPreview(null)} className="rounded-full border border-choco-line p-2 text-cream-muted hover:text-gold" aria-label="Close preview" data-testid="email-preview-close">
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            <div className="mt-3 flex gap-2">
              {["html", "text"].map((t) => (
                <button key={t} onClick={() => setTab(t)} className={`rounded-full px-3.5 py-1 text-xs font-bold uppercase ${tab === t ? "bg-gold text-choco" : "border border-choco-line text-cream-muted"}`} data-testid={`email-preview-tab-${t}`}>{t}</button>
              ))}
            </div>
            {tab === "html" ? (
              <iframe title="Email HTML preview" srcDoc={preview.html} className="mt-3 min-h-[50vh] w-full flex-1 rounded-2xl bg-white" sandbox="" data-testid="email-preview-frame" />
            ) : (
              <pre className="mt-3 min-h-[50vh] flex-1 overflow-auto whitespace-pre-wrap rounded-2xl border border-choco-line bg-choco p-4 text-xs text-cream-text" data-testid="email-preview-text">{preview.text}</pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
